import { ADVERTISING_AUTH_APP } from '../api/constants';

// Marker for the loop guard below. Lives in RAW sessionStorage, not a redux slice.
// redux-persist rehydration runs asynchronously behind PersistGate, so a marker written
// to redux might not have been flushed to storage yet when window.location.assign tears
// the page down below; a synchronous, direct sessionStorage write does not have that
// problem and is guaranteed to be there on the way back in.
const REAUTH_MARKER = 'reauthAttempted';

export type AuthMode = 'sso' | 'password';

// Auth-mode hint: which login path this browser last used, so a fresh tab with no
// persisted user (sessionStorage is per-tab, see SEC-778 / GC-2009) can pick the right
// behaviour instead of guessing. This is a mode label, not a credential: it says "sso" or
// "password", nothing about who the user is or any token. Storing that in localStorage
// does not weaken the SEC-778 posture that moved the auth slice itself to sessionStorage,
// because there is nothing here worth stealing.
//
// Deliberately NOT `persist:root`: src/store.ts removes that localStorage key on every
// page load as part of the SEC-778 cleanup, which would erase this hint immediately.
const AUTH_MODE_KEY = 'pc-auth-mode';

/**
 * True when this tab has already bounced through SSO and come back still without a
 * usable token. Callers use this to render a static error instead of redirecting again.
 *
 * Why this exists: a user who reaches advertising-apps-auth but has no Publisher Center
 * access (or whose session there is also stale) gets sent back here with no token,
 * forever. An endless bounce between two apps is worse than a dead end, because the user
 * cannot read anything or press Back.
 */
export const hasAttemptedReauth = (): boolean => {
    try {
        return sessionStorage.getItem(REAUTH_MARKER) === '1';
    } catch (e) {
        // sessionStorage access can throw in some privacy modes. Treat that as "already
        // attempted" so we fail to a readable page instead of an unguarded redirect loop.
        return true;
    }
};

/**
 * Removes the loop-guard marker. Call this as soon as a token arrives (e.g. the ?jwt=
 * round trip from advertising-apps-auth) so a later expiry in the same tab is allowed to
 * re-auth again instead of being treated as a repeat of a failed attempt.
 */
export const clearReauthMarker = (): void => {
    try {
        sessionStorage.removeItem(REAUTH_MARKER);
    } catch (e) {
        // Nothing to do; the marker is best-effort.
    }
};

const setReauthMarker = (): boolean => {
    try {
        sessionStorage.setItem(REAUTH_MARKER, '1');
    } catch (e) {
        // If we cannot write the marker we cannot detect a loop on the way back, so the
        // caller must not navigate at all. Returning false makes redirectToAuth a no-op
        // and the caller falls through to its own error state instead of risking an
        // unguarded loop.
        return false;
    }
    return true;
};

/**
 * Strips the jwt query param out of the current URL so a token can never travel inside a
 * redirectTo.
 */
const currentPathWithoutJwt = (): string => {
    const url = new URL(window.location.href);
    url.searchParams.delete('jwt');
    return url.pathname + url.search;
};

/**
 * Sends the browser through SSO and back to the page it asked for.
 *
 * A tab opened with cmd+click, middle-click, target="_blank", or a pasted URL gets no
 * copy of the sessionStorage-backed auth state (that storage engine move is intentional,
 * see SEC-778 / GC-2009), so it starts with no token even though the user is signed in
 * elsewhere. Rather than dead-ending on /login, re-run SSO: advertising-apps-auth mints a
 * fresh token and redirects back to `redirectTo`, so the user lands on the page they
 * actually clicked.
 *
 * Returns false when the caller should render an error instead of redirecting, either
 * because this tab already tried once (see hasAttemptedReauth), or because the loop-guard
 * marker could not be written at all.
 *
 * This module has no dispatch access, so it does not clear persisted auth state itself.
 * Callers should dispatch `logout()` (or purge the persistor) before or alongside calling
 * this, so a stale expired token does not survive into the blocked state and 401 every
 * later request this app makes.
 */
export const redirectToAuth = (): boolean => {
    if (hasAttemptedReauth()) {
        return false;
    }
    if (!setReauthMarker()) {
        return false;
    }

    const target = `${ADVERTISING_AUTH_APP}?redirectTo=${encodeURIComponent(
        window.location.origin + currentPathWithoutJwt(),
    )}`;

    window.location.assign(target);
    return true;
};

/**
 * Logout destination for a deliberate logout: the applications picker, with no
 * redirectTo. Sending a redirectTo here would authenticate the user straight back into
 * the app they just chose to leave.
 */
export const redirectToAuthPicker = (): void => {
    clearReauthMarker();
    window.location.assign(ADVERTISING_AUTH_APP);
};

export const setAuthMode = (mode: AuthMode): void => {
    try {
        localStorage.setItem(AUTH_MODE_KEY, mode);
    } catch (e) {
        // Best-effort hint; nothing to do if storage is unavailable.
    }
};

export const getAuthMode = (): AuthMode | null => {
    try {
        const value = localStorage.getItem(AUTH_MODE_KEY);
        return value === 'sso' || value === 'password' ? value : null;
    } catch (e) {
        return null;
    }
};
